<script setup>
/* eslint-disable no-undef */
import { ref, watch, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  },
  confirmLabel: {
    type: String,
    default: 'Confirm'
  },
  cancelLabel: {
    type: String,
    default: 'Cancel'
  },
  confirmColor: {
    type: String,
    default: 'red' // 'red' | 'emerald'
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const modalRef = ref(null)
const titleId = `confirm-modal-title-${Math.random().toString(36).slice(2, 10)}`

function trapFocus (event) {
  if (!modalRef.value) return
  if (event.key !== 'Tab') return

  const focusable = modalRef.value.querySelectorAll(
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )
  if (focusable.length === 0) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

// Prevent body scroll when modal is open + manage focus trap
watch(() => props.visible, async (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
  if (val) {
    await nextTick()
    const firstButton = modalRef.value?.querySelector('button')
    firstButton?.focus()
    document.addEventListener('keydown', trapFocus)
  } else {
    document.removeEventListener('keydown', trapFocus)
  }
})

// Safety net: restore overflow + listeners if component is destroyed while still open
onUnmounted(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', trapFocus)
})
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-theme-text/50"
          @click="emit('cancel')"
        />

        <!-- Dialog -->
        <div
          ref="modalRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          class="relative bg-theme-surface dark:bg-theme-surface rounded-lg shadow-theme-dialog max-w-sm w-full p-6"
        >
          <h3
            v-if="title"
            :id="titleId"
            class="text-lg font-semibold text-theme-text mb-2"
          >
            {{ title }}
          </h3>
          <p
            v-if="message"
            class="text-sm text-theme-text-secondary mb-6"
          >
            {{ message }}
          </p>
          <slot />
          <div class="flex justify-end gap-3 mt-4">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-md
                     text-theme-text-secondary
                     bg-theme-bg-subtle dark:bg-theme-surface-raised
                     hover:bg-theme-surface-raised dark:hover:bg-theme-surface-raised
                     transition-colors"
              @click="emit('cancel')"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-md text-theme-text-inverse transition-colors"
              :class="{
                'bg-theme-palette-red hover:bg-theme-palette-red': confirmColor === 'red',
                'bg-theme-palette-emerald hover:bg-theme-palette-emerald': confirmColor === 'emerald'
              }"
              @click="emit('confirm')"
            >
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
